import React, { useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Plus, Trash2, Edit3, Phone, Mail, Star, Users, AlertTriangle, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';

const API = process.env.REACT_APP_BACKEND_URL;

const RELATIONSHIP_OPTIONS = [
  { value: 'sponsor', label: 'TSF Sponsor' },
  { value: 'family', label: 'Family' },
  { value: 'partner', label: 'Partner' },
  { value: 'friend', label: 'Friend' },
  { value: 'therapist', label: 'Therapist' },
  { value: 'doctor', label: 'Doctor' },
  { value: 'other', label: 'Other' },
];

const EMPTY = {
  name: '',
  relationship: 'family',
  phone: '',
  email: '',
  notes: '',
  is_primary_sponsor: false,
};

/**
 * EmergencyContactsCard
 *
 * Allows a user to record up to 5 emergency contacts (TSF Sponsor + family +
 * friends + therapist etc). Tudor bot uses these contacts when the mental
 * health check-in indicates the user is in distress and the protocol
 * recommends reaching out to a human.
 *
 * Privacy posture (v1):
 *   - Contacts are visible to the user only.
 *   - When Tudor detects distress, contacts are SURFACED to the user in chat
 *     (the user calls/texts them manually). Auto-SMS opt-in will follow.
 */
function EmergencyContactsCard() {
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | contact_id
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/api/user/emergency-contacts`);
      const d = await r.json();
      setContacts(Array.isArray(d.contacts) ? d.contacts : []);
    } catch {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const startNew = () => {
    if (contacts.length >= 5) {
      toast.error('Maximum of 5 contacts. Remove one to add another.');
      return;
    }
    setForm(EMPTY);
    setEditing('new');
  };

  const startEdit = (c) => {
    setForm({
      name: c.name || '',
      relationship: c.relationship || 'family',
      phone: c.phone || '',
      email: c.email || '',
      notes: c.notes || '',
      is_primary_sponsor: !!c.is_primary_sponsor,
    });
    setEditing(c.contact_id);
  };

  const cancel = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Name and phone are required');
      return;
    }
    setSaving(true);
    try {
      const url =
        editing === 'new'
          ? `${API}/api/user/emergency-contacts`
          : `${API}/api/user/emergency-contacts/${editing}`;
      const method = editing === 'new' ? 'POST' : 'PUT';
      const r = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Could not save contact');
      }
      toast.success(editing === 'new' ? 'Contact added' : 'Contact updated');
      await refresh();
      cancel();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name}?`)) return;
    try {
      const r = await authFetch(`${API}/api/user/emergency-contacts/${c.contact_id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Could not remove');
      toast.success('Removed');
      await refresh();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const labelOf = (rel) =>
    RELATIONSHIP_OPTIONS.find((o) => o.value === rel)?.label || rel;

  return (
    <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden" data-testid="emergency-contacts-card">
      <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2">
        <Users className="w-4 h-4 text-zinc-700" />
        <h3 className="text-sm font-bold text-zinc-950 uppercase tracking-wider flex-1">
          Contact Person / Family
        </h3>
        <span className="text-[10px] text-zinc-400 font-semibold">{contacts.length}/5</span>
      </div>

      <div className="px-4 py-3 bg-amber-50 border-b border-amber-100">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-900 leading-relaxed">
            Add up to 5 people Tudor can surface to you if a mental-health check-in indicates
            you are struggling. We strongly recommend including <strong>a TSF Sponsor</strong> as
            your primary contact. Tudor will <em>never</em> contact them automatically: these
            are the people <strong>you</strong> can reach out to in distress.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-zinc-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100" data-testid="contacts-list">
          {contacts.length === 0 && editing !== 'new' && (
            <li className="px-4 py-6 text-center text-xs text-zinc-500" data-testid="contacts-empty-state">
              No contacts yet. Tap below to add your TSF Sponsor first.
            </li>
          )}
          {contacts.map((c) =>
            editing === c.contact_id ? (
              <li key={c.contact_id} className="px-4 py-3 bg-zinc-50">
                <ContactForm form={form} setForm={setForm} onSave={save} onCancel={cancel} saving={saving} />
              </li>
            ) : (
              <li key={c.contact_id} className="px-4 py-3 flex items-start gap-3" data-testid={`contact-${c.contact_id.slice(0, 8)}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                    {c.name}
                    {c.is_primary_sponsor && (
                      <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400" title="Primary TSF Sponsor" />
                    )}
                  </p>
                  <p className="text-[11px] text-zinc-500 uppercase tracking-wider">{labelOf(c.relationship)}</p>
                  <div className="mt-1 flex flex-col gap-0.5">
                    <a href={`tel:${c.phone}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {c.phone}
                    </a>
                    {c.email && (
                      <a href={`mailto:${c.email}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {c.email}
                      </a>
                    )}
                    {c.notes && <p className="text-[11px] text-zinc-500 italic">{c.notes}</p>}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => startEdit(c)}
                    className="p-1.5 text-zinc-400 hover:text-zinc-700"
                    aria-label={`Edit ${c.name}`}
                    data-testid={`edit-contact-${c.contact_id.slice(0, 8)}`}
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => remove(c)}
                    className="p-1.5 text-zinc-400 hover:text-red-600"
                    aria-label={`Remove ${c.name}`}
                    data-testid={`remove-contact-${c.contact_id.slice(0, 8)}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            )
          )}

          {editing === 'new' && (
            <li className="px-4 py-3 bg-zinc-50">
              <ContactForm form={form} setForm={setForm} onSave={save} onCancel={cancel} saving={saving} />
            </li>
          )}
        </ul>
      )}

      {editing === null && contacts.length < 5 && (
        <div className="px-4 py-3 border-t border-zinc-100">
          <Button onClick={startNew} variant="outline" className="w-full h-10" data-testid="add-contact-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add a contact
          </Button>
        </div>
      )}
    </div>
  );
}

function ContactForm({ form, setForm, onSave, onCancel, saving }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wider text-zinc-700">Contact details</p>
        <button onClick={onCancel} className="text-zinc-400 hover:text-zinc-700" aria-label="Cancel">
          <X className="w-4 h-4" />
        </button>
      </div>
      <input
        type="text"
        placeholder="Name *"
        value={form.name}
        onChange={(e) => set('name', e.target.value)}
        className="w-full h-10 px-3 text-sm bg-white border border-zinc-300 focus:border-zinc-950 rounded-md"
        maxLength={80}
        data-testid="contact-name-input"
      />
      <select
        value={form.relationship}
        onChange={(e) => set('relationship', e.target.value)}
        className="w-full h-10 px-3 text-sm bg-white border border-zinc-300 focus:border-zinc-950 rounded-md"
        data-testid="contact-relationship-select"
      >
        {RELATIONSHIP_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <input
        type="tel"
        placeholder="Phone (e.g. +61 400 000 000) *"
        value={form.phone}
        onChange={(e) => set('phone', e.target.value)}
        className="w-full h-10 px-3 text-sm bg-white border border-zinc-300 focus:border-zinc-950 rounded-md"
        maxLength={40}
        data-testid="contact-phone-input"
      />
      <input
        type="email"
        placeholder="Email (optional)"
        value={form.email}
        onChange={(e) => set('email', e.target.value)}
        className="w-full h-10 px-3 text-sm bg-white border border-zinc-300 focus:border-zinc-950 rounded-md"
        maxLength={120}
        data-testid="contact-email-input"
      />
      <input
        type="text"
        placeholder="Notes (optional, e.g. best to call after 6pm)"
        value={form.notes}
        onChange={(e) => set('notes', e.target.value)}
        className="w-full h-10 px-3 text-sm bg-white border border-zinc-300 focus:border-zinc-950 rounded-md"
        maxLength={300}
        data-testid="contact-notes-input"
      />
      <label className="flex items-center gap-2 text-xs text-zinc-700 py-1 cursor-pointer">
        <input
          type="checkbox"
          checked={form.is_primary_sponsor}
          onChange={(e) => set('is_primary_sponsor', e.target.checked)}
          className="w-4 h-4"
          data-testid="contact-sponsor-checkbox"
        />
        Mark as my primary TSF Sponsor (only one allowed)
      </label>
      <div className="flex gap-2 pt-1">
        <Button onClick={onSave} disabled={saving} className="flex-1 bg-zinc-950 hover:bg-zinc-800 text-white h-10" data-testid="save-contact-btn">
          {saving ? 'Saving…' : 'Save contact'}
        </Button>
        <Button onClick={onCancel} variant="outline" className="h-10" disabled={saving}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export default EmergencyContactsCard;
